// Rules for VALIDATION_CONSTRAINTS.md section 5 — start event content & topology.
// Cardinality (5.2) is covered by 3.1 and intentionally skipped here.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import { getFamily, labelOf } from '../helpers';

// 5.1: Every process must contain at least one StartEvent family node.
//      Subprocesses may legitimately be entered via Error/Escalation start events,
//      but those also belong to the StartEvent family so this single check covers both.
const rule_5_1_StartEventPresence: ValidationRule = {
  id: '5.1',
  description: 'Every process must have at least one StartEvent',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const starts = process.nodes.filter(n => getFamily(n.type) === 'StartEvent');
    if (starts.length === 0) {
      return [{
        severity: 'error',
        rule: '5.1',
        message: `Process "${process.name}" has no StartEvent.`,
        processId: process.id,
      }];
    }
    return [];
  },
};
registerRule(rule_5_1_StartEventPresence);

// 5.2: Skipped — start-event cardinality (0 incoming, 1 outgoing) is enforced by rule 3.1.

// 5.3: MessageStartEvent requires a non-empty messageName.
const rule_5_3_MessageStartEventName: ValidationRule = {
  id: '5.3',
  description: 'MessageStartEvent requires a non-empty messageName',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'MessageStartEvent') continue;
      const name = (n.data?.messageName ?? '').toString().trim();
      if (!name) {
        issues.push({
          severity: 'error',
          rule: '5.3',
          message: `${labelOf(n)} requires a non-empty messageName.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_5_3_MessageStartEventName);

// 5.4: TimerStartEvent requires timerDefinition or timerDate.
const rule_5_4_TimerStartEventConfig: ValidationRule = {
  id: '5.4',
  description: 'TimerStartEvent requires a timerDefinition or timerDate',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'TimerStartEvent') continue;
      const def = (n.data?.timerDefinition ?? '').toString().trim();
      const date = (n.data?.timerDate ?? '').toString().trim();
      if (!def && !date) {
        issues.push({
          severity: 'error',
          rule: '5.4',
          message: `${labelOf(n)} requires a timerDefinition or timerDate.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_5_4_TimerStartEventConfig);

// 5.5: SignalStartEvent requires a non-empty signalName.
const rule_5_5_SignalStartEventName: ValidationRule = {
  id: '5.5',
  description: 'SignalStartEvent requires a non-empty signalName',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'SignalStartEvent') continue;
      const name = (n.data?.signalName ?? '').toString().trim();
      if (!name) {
        issues.push({
          severity: 'error',
          rule: '5.5',
          message: `${labelOf(n)} requires a non-empty signalName.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_5_5_SignalStartEventName);

// 5.6: ErrorStartEvent and EscalationStartEvent may only appear inside a sub-process.
const rule_5_6_ErrorEscalationStartOnlyInSubProcess: ValidationRule = {
  id: '5.6',
  description: 'ErrorStartEvent/EscalationStartEvent are only valid inside a sub-process',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    if (process.isSubProcess) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type === 'ErrorStartEvent' || n.type === 'EscalationStartEvent') {
        issues.push({
          severity: 'error',
          rule: '5.6',
          message: `${labelOf(n)} is only allowed inside a sub-process (process "${process.name}" is top-level).`,
          nodeIds: [n.id],
          processId: process.id,
        });
      }
    }
    return issues;
  },
};
registerRule(rule_5_6_ErrorEscalationStartOnlyInSubProcess);

// 5.7: Root process should have exactly one primary start entry.
//      Primary = Blank/Form/Message/Timer/Signal (Error/Escalation are sub-process-only).
//      Warning if count > 1 (0 case already blocked by 5.1 as error).
const PRIMARY_START_TYPES = new Set([
  'BlankStartEvent',
  'FormStartEvent',
  'ApiStartEvent',
  'UiStartEvent',
  'MessageStartEvent',
  'TimerStartEvent',
  'SignalStartEvent',
]);

const rule_5_7_SinglePrimaryRootStart: ValidationRule = {
  id: '5.7',
  description: 'Root process should have exactly one primary start entry',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    if (process.isSubProcess) return [];
    const primaries = process.nodes.filter(n => n.type && PRIMARY_START_TYPES.has(n.type));
    if (primaries.length > 1) {
      return [{
        severity: 'warning',
        rule: '5.7',
        message: `Root process "${process.name}" has ${primaries.length} primary start events; exactly one is recommended.`,
        nodeIds: primaries.map(n => n.id),
        processId: process.id,
      }];
    }
    return [];
  },
};
registerRule(rule_5_7_SinglePrimaryRootStart);
